/**
 * HeaterCard — native rewrite of the legacy HeatingSystemItem. Presentational: reads
 * a `HeaterVM` (rich working state with a STABLE local id + stable-id billing rows —
 * C6) and emits partial updates + billing CRUD to its parent panel. No store/context
 * coupling. Billing rows are keyed by their stable `localId` (never array index — C6).
 */

import React, { useMemo, useState } from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Grow from '@mui/material/Grow'
import { TransitionGroup } from 'react-transition-group'

import { GRAY_400, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import {
  FuelType,
  HotWaterGenerationType,
  heaterConsumptionUnitList
} from 'constants/purchaseFlow/consumptionCertificateTypes'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Label } from 'components/common/Label'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDatePicker } from 'components/common/MUIDatePicker'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIFloatNumberField } from 'components/common/MUIFloatNumberField'
import { MUIRadio } from 'components/common/MUIRadio'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'

import type { BillingPeriodVM, HeaterVM } from '../consumptionCertificate.codec'
import { BillingPeriodCard } from './BillingPeriodCard'
import { MUIIconButton } from 'components/common/MUIIconButton'

const MAX_BILLING_PERIODS = 4

export interface HeaterCardProps {
  heater: HeaterVM
  index: number
  onRemove: () => void
  onUpdate: (partial: Partial<HeaterVM>) => void
  onAddBillingPeriod: () => void
  onUpdateBillingPeriod: (billingLocalId: string, partial: Partial<BillingPeriodVM>) => void
  onRemoveBillingPeriod: (billingLocalId: string) => void
}

export const HeaterCard: React.FC<HeaterCardProps> = ({
  heater,
  index,
  onRemove,
  onUpdate,
  onAddBillingPeriod,
  onUpdateBillingPeriod,
  onRemoveBillingPeriod,
}) => {
  const { t } = useTranslation(['heating_system'])

  const [showError, setShowError] = useState(false)
  const handleOnInputBlur = () => {
    if (!showError) setShowError(true)
  }

  const heatingUnits = useMemo(() => (heater.fuelType ? heaterConsumptionUnitList[heater.fuelType] : []), [heater.fuelType])
  const showUnitCaptureError = showError && !heater.unitCaptureType
  const billingPeriods = heater.billingPeriodItems

  return (
    <SectionedBorderBox
      title={
        <Stack direction="row" justifyContent="space-between" alignItems="center">

          <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
            {`${t('heating_system_title')} #${index + 1}`}
          </Typography>

          {index > 0 &&
            <MUIIconButton variant="plain" size="lg" icon={<CloseRoundedIcon />} ariaLabel="delete-heating-system" onClick={onRemove} />
          }

        </Stack>
      }
    >

      <Stack alignItems="flex-start" gap={2}>

        <MUIDatePicker
          required
          view="year"
          views={['year']}
          label={t('generator_year_manufacture')}
          value={heater.yearOfManufacture}
          isError={showError && !heater.yearOfManufacture}
          onInputBlur={handleOnInputBlur}
          onChange={(newValue) => onUpdate({ yearOfManufacture: newValue })}
        />

        <Stack direction="row" flexWrap="wrap" width="100%" gap={2}>

          {/* FUEL */}
          <Box width={{ xs: '100%', sm: '40rem', lg: '50rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_, onClick) => (
                <MUIDropdownInput
                  required
                  label={t('fuel')}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(heater.fuelType ? `fuel_type.${heater.fuelType}` : 'select_fuel_option')}
                  isError={showError && !heater.fuelType}
                  onClick={onClick}
                />
              )}
            >
              {Object.values(FuelType).map((fuelTypeItem) => (
                <MUIDropdownItem
                  key={fuelTypeItem}
                  onClick={() => onUpdate({ fuelType: fuelTypeItem, primaryEnergyFactor: '', unitCaptureType: undefined })}
                >
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`fuel_type.${fuelTypeItem}`)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Box>

          {/* HOT WATER */}
          <Box width={{ xs: '100%', sm: '40rem', lg: '50rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_, onClick) => (
                <MUIDropdownInput
                  required
                  label={t('hot_water')}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(heater.hotWaterType ? `hot_water_type.${heater.hotWaterType}` : 'select_hot_water_option')}
                  isError={showError && !heater.hotWaterType}
                  onClick={onClick}
                />
              )}
            >
              <Box>
                {Object.values(HotWaterGenerationType).map((hotWaterTypeItem) => (
                  <MUIDropdownItem key={hotWaterTypeItem} onClick={() => onUpdate({ hotWaterType: hotWaterTypeItem })}>
                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                      {t(`hot_water_type.${hotWaterTypeItem}`)}
                    </Typography>
                  </MUIDropdownItem>
                ))}
              </Box>
            </MUIDropdown>
          </Box>

        </Stack>

        {/* UNIT CAPTURE */}
        <Stack gap={0.6}>
          {heatingUnits.length > 0 && <>

            <Label text={t('unit_capture')} required isError={showUnitCaptureError} />

            {heatingUnits.map((unitCaptureTypeItem) => (
              <Stack key={unitCaptureTypeItem} gap=".8rem" flexDirection="row">

                <MUIRadio
                  checked={heater.unitCaptureType === unitCaptureTypeItem}
                  onChange={() => onUpdate({ unitCaptureType: unitCaptureTypeItem })}
                />

                <Typography
                  variant="text-md"
                  fontWeight={500}
                  color={GRAY_900}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onUpdate({ unitCaptureType: unitCaptureTypeItem })}
                >
                  {t(`unit_capture_type.${unitCaptureTypeItem}`)}
                </Typography>

              </Stack>
            ))}

          </>}
        </Stack>

        {/* PRIMARY ENERGY FACTOR — district heating only */}
        {heater.fuelType === FuelType.DISTRICT_HEATING &&
          <MUIFloatNumberField
            required
            inputBoxWidth="25%"
            label={t('primary_energy_factor')}
            value={heater.primaryEnergyFactor}
            isError={showError && !heater.primaryEnergyFactor}
            onBlur={handleOnInputBlur}
            onChange={(value) => onUpdate({ primaryEnergyFactor: value })}
          />
        }

      </Stack>

      {/* CONSUMPTION DATA */}
      <Stack gap={0.6} marginTop={2}>

        <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
          {t('consumption_data')}
        </Typography>

        <Typography variant="text-sm" color={GRAY_700}>
          {t('consumption_data_description')}
        </Typography>

        <MUIDivider margin={8} />

        <Box flexGrow={1}>
          <Grid container spacing={2}>

            <TransitionGroup component={null}>
              {billingPeriods.map((billing, billingIndex) => (
                <Grow key={billing.localId} in timeout={{ enter: 300, exit: 500 }}>
                  <Grid item xs={12} md={6}>
                    <BillingPeriodCard
                      billing={billing}
                      index={billingIndex}
                      hotWaterType={heater.hotWaterType}
                      unit={heater.unitCaptureType}
                      showErrors={showError}
                      onInputBlur={handleOnInputBlur}
                      onUpdate={(partial) => onUpdateBillingPeriod(billing.localId, partial)}
                      onRemove={() => onRemoveBillingPeriod(billing.localId)}
                    />
                  </Grid>
                </Grow>
              ))}
            </TransitionGroup>

            {/* Add billing period — up to 4 */}
            <Grid item xs={12} md={6}>
              <BorderBoxWrapper
                borderColor={GRAY_400}
                sx={{
                  padding: 2,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '18.8rem',
                  borderStyle: 'dashed',
                  justifyContent: 'center',
                }}
              >
                <MUIButton
                  size="md"
                  type="secondaryNoBorder"
                  disabled={billingPeriods.length >= MAX_BILLING_PERIODS}
                  startIcon={<AddIcon fontSize="large" />}
                  onClick={onAddBillingPeriod}
                >
                  {t('add_billing_period')}
                </MUIButton>
              </BorderBoxWrapper>
            </Grid>

          </Grid>
        </Box>

      </Stack>

    </SectionedBorderBox>
  )
}
